import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Divider,
  LinearProgress, Paper, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import PageHeader from '../components/PageHeader';
import { useProductTour } from '../tour/TourProvider';
import { getActiveProjectId, subscribeProjectChange } from '../services/projectContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
type RelatedFeature = [string, string];
type Shot = { label: string; caption: string };
type GuideStep = { title: string; detail: string; action: string | null; result: string | null; tips: string[]; screenshot: Shot | null };
type CommonIssue = { question: string; answer: string };
type Article = {
  id: string; title: string; summary: string; category: string; body: string[];
  why: string | null; steps: GuideStep[]; tips: string[]; issues: CommonIssue[];
  screenshot: Shot | null; read_time: string | null; featured: boolean;
  related_features: RelatedFeature[]; updated_at: string;
};
type CommonTask = { id: string; title: string; description: string; icon: string; article: string; route: string; route_label: string };
type WorkflowStage = { step: string; detail: string; route: string };
type Viewer = { role_label: string; project_name: string | null; is_platform_admin: boolean };

const categories = ['All', 'Getting Started', 'Managing Consents', 'Ownership & Governance', 'Administration', 'Reporting & Risk', 'FAQs'];
const ACCENTS: Record<string, string> = {
  'Getting Started': '#2450C6',
  'Managing Consents': '#0F766E',
  'Ownership & Governance': '#4F46E5',
  Administration: '#B45309',
  'Reporting & Risk': '#BE123C',
  FAQs: '#475569',
};
const accentFor = (category: string) => ACCENTS[category] || '#2450C6';
const TASK_ICONS: Record<string, React.ReactElement> = {
  create: <NoteAddOutlinedIcon />,
  import: <UploadFileOutlinedIcon />,
  ownership: <AssignmentIndOutlinedIcon />,
  lifecycle: <TimelineOutlinedIcon />,
  review: <FactCheckOutlinedIcon />,
  permissions: <AdminPanelSettingsOutlinedIcon />,
  coverage: <GroupsOutlinedIcon />,
};
const PROGRESS_KEY = 'assertion.help.progress';
const readProgress = (): Record<string, number[]> => {
  try { return JSON.parse(window.localStorage.getItem(PROGRESS_KEY) || '{}'); } catch { return {}; }
};
const writeProgress = (value: Record<string, number[]>) => {
  try { window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(value)); } catch { /* storage unavailable */ }
};

function ScreenshotSlot({ shot, accent }: { shot: Shot; accent: string }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mt: 1.5, px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: alpha(accent, 0.04), borderLeft: `3px solid ${alpha(accent, 0.45)}` }}>
      <ImageOutlinedIcon sx={{ fontSize: 18, color: alpha(accent, 0.7), mt: 0.2 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>{shot.label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.55 }}>{shot.caption}</Typography>
      </Box>
    </Stack>
  );
}

function TipsPanel({ tips, title = 'Tips' }: { tips: string[]; title?: string }) {
  if (!tips.length) return null;
  return (
    <Box sx={{ p: 1.75, borderRadius: 1.5, bgcolor: alpha('#D97706', 0.06), border: '1px solid', borderColor: alpha('#D97706', 0.25) }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <LightbulbOutlinedIcon sx={{ fontSize: 17, color: '#B45309' }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: '#92400E', letterSpacing: '0.03em' }}>{title.toUpperCase()}</Typography>
      </Stack>
      <Stack spacing={0.5}>
        {tips.map((tip, index) => <Typography key={index} variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>• {tip}</Typography>)}
      </Stack>
    </Box>
  );
}

function StepCard({ step, index, accent, done, onToggle }: { step: GuideStep; index: number; accent: string; done: boolean; onToggle: () => void }) {
  return (
    <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 } }}>
      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, bgcolor: done ? accent : alpha(accent, 0.12), color: done ? '#FFFFFF' : accent, border: '1px solid', borderColor: alpha(accent, 0.35) }}>
          {done ? <CheckOutlinedIcon sx={{ fontSize: 17 }} /> : index + 1}
        </Box>
        <Box sx={{ flexGrow: 1, width: '1px', bgcolor: 'divider', my: 0.5, minHeight: 12 }} />
      </Stack>
      <Box sx={{ minWidth: 0, flexGrow: 1, pb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600} sx={{ textDecoration: done ? 'line-through' : 'none', color: done ? 'text.secondary' : 'text.primary' }}>{step.title}</Typography>
          <Button size="small" onClick={onToggle} startIcon={done ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />} sx={{ flexShrink: 0, color: done ? accent : 'text.secondary' }}>
            {done ? 'Done' : 'Mark done'}
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.7 }}>{step.detail}</Typography>
        {(step.action || step.result) && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {step.action && (
              <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: alpha(accent, 0.06), border: '1px solid', borderColor: alpha(accent, 0.2) }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TouchAppOutlinedIcon sx={{ fontSize: 17, color: accent, mt: 0.2 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: accent, letterSpacing: '0.03em', display: 'block' }}>WHERE TO GO</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.action}</Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            {step.result && (
              <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: alpha('#16A34A', 0.05), border: '1px solid', borderColor: alpha('#16A34A', 0.22) }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleIcon sx={{ fontSize: 17, color: '#15803D', mt: 0.2 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#15803D', letterSpacing: '0.03em', display: 'block' }}>WHAT HAPPENS NEXT</Typography>
                    <Typography variant="body2" color="text.secondary">{step.result}</Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
        {step.screenshot && <ScreenshotSlot shot={step.screenshot} accent={accent} />}
        {step.tips.length > 0 && <Box sx={{ mt: 1.5 }}><TipsPanel tips={step.tips} /></Box>}
      </Box>
    </Box>
  );
}

function GuideDetail({ article, viewer, onBack }: { article: Article; viewer: Viewer | null; onBack: () => void }) {
  const accent = accentFor(article.category);
  const [done, setDone] = useState<number[]>([]);
  useEffect(() => { setDone(readProgress()[article.id] || []); }, [article.id]);
  const toggleStep = (index: number) => setDone(previous => {
    const next = previous.includes(index) ? previous.filter(item => item !== index) : [...previous, index];
    writeProgress({ ...readProgress(), [article.id]: next });
    return next;
  });
  const completed = done.filter(index => index < article.steps.length).length;
  const percent = article.steps.length ? Math.round((completed / article.steps.length) * 100) : 0;
  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>All guidance</Button>
      <Paper sx={{ overflow: 'hidden', mb: 3 }}>
        <Box sx={{ height: 4, bgcolor: accent }} />
        <Box sx={{ p: { xs: 2.5, md: 3.5 }, background: `linear-gradient(180deg, ${alpha(accent, 0.07)} 0%, rgba(255,255,255,0) 100%)` }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip size="small" label={article.category} sx={{ bgcolor: alpha(accent, 0.12), color: accent, fontWeight: 600 }} />
            {article.read_time && <Chip size="small" variant="outlined" icon={<ScheduleOutlinedIcon />} label={article.read_time} />}
            {article.steps.length > 0 && <Chip size="small" variant="outlined" icon={<MenuBookOutlinedIcon />} label={`${article.steps.length} steps`} />}
          </Stack>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, mb: 1 }}>{article.title}</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760 }}>{article.summary}</Typography>
        </Box>
      </Paper>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' }, gap: 3, alignItems: 'start' }}>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="overline" color="text.secondary">Overview</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {article.body.map((paragraph, index) => <Typography key={index} sx={{ lineHeight: 1.75 }}>{paragraph}</Typography>)}
            </Stack>
            {article.screenshot && <ScreenshotSlot shot={article.screenshot} accent={accent} />}
          </Paper>
          {article.why && (
            <Paper sx={{ p: { xs: 2.5, md: 3 }, borderLeft: `4px solid ${accent}` }}>
              <Typography variant="overline" sx={{ color: accent }}>Why it matters</Typography>
              <Typography sx={{ mt: 1, lineHeight: 1.75 }}>{article.why}</Typography>
            </Paper>
          )}
          {article.steps.length > 0 && (
            <Paper sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="overline" color="text.secondary">Follow these steps</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mb: 2.5 }}>Each step shows where to go and what happens afterwards. Take what you need and return to your work.</Typography>
              {article.steps.map((step, index) => (
                <StepCard key={step.title} step={step} index={index} accent={accent} done={done.includes(index)} onToggle={() => toggleStep(index)} />
              ))}
            </Paper>
          )}
          {article.issues.length > 0 && (
            <Paper sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="overline" color="text.secondary">Common issues</Typography>
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                {article.issues.map(issue => (
                  <Box key={issue.question} sx={{ p: 1.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: alpha(accent, 0.02) }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <HelpOutlineIcon fontSize="small" sx={{ color: accent, mt: 0.25 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600}>{issue.question}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.7 }}>{issue.answer}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
        <Stack spacing={2} sx={{ minWidth: 0, position: { lg: 'sticky' }, top: { lg: 16 } }}>
          {article.tips.length > 0 && <Paper sx={{ p: 2.5 }}><TipsPanel tips={article.tips} title="Good practice" /></Paper>}
          {article.related_features.length > 0 && (
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="overline" color="text.secondary">Related features</Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {article.related_features.map(([label, path]) => (
                  <Button key={`${label}-${path}`} component={RouterLink} to={path} variant="outlined" size="small" endIcon={<ChevronRightIcon />} sx={{ justifyContent: 'space-between' }}>{label}</Button>
                ))}
              </Stack>
            </Paper>
          )}
          <Paper sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <BadgeOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Viewing guidance for</Typography>
            </Stack>
            <Typography variant="subtitle2">{viewer?.role_label || 'Your account'}</Typography>
            {viewer?.project_name && <Typography variant="caption" color="text.secondary">{viewer.project_name}</Typography>}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">Updated {article.updated_at}</Typography>
          </Paper>
          {article.steps.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.75, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.03em' }}>GUIDE CHECKLIST</Typography>
                <Typography variant="caption" color="text.secondary">{completed}/{article.steps.length}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.5 }}>Optional. Mark steps as you go if you are working through the guide alongside the product.</Typography>
              <LinearProgress variant="determinate" value={percent} sx={{ mt: 1.25, height: 4, borderRadius: 2, bgcolor: alpha(accent, 0.1), '& .MuiLinearProgress-bar': { bgcolor: completed ? accent : 'transparent' } }} />
              {completed > 0 && <Button size="small" sx={{ mt: 0.5, px: 0, minWidth: 0 }} onClick={() => { setDone([]); writeProgress({ ...readProgress(), [article.id]: [] }); }}>Clear</Button>}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default function HelpGuidance() {
  const { startTour } = useProductTour();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [tasks, setTasks] = useState<CommonTask[]>([]);
  const [workflow, setWorkflow] = useState<WorkflowStage[]>([]);
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [category, setCategory] = useState(searchParams.get('category') || 'All');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(searchParams.get('article'));
  const [error, setError] = useState('');
  const load = () => {
    const project = getActiveProjectId();
    fetch(`${API_BASE}/help/articles${project ? `?project_id=${project}` : ''}`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Help content could not be loaded.')))
      .then(data => {
        setArticles(data.articles || []);
        setTasks(data.common_tasks || []);
        setWorkflow(data.workflow || []);
        setViewer(data.viewer || null);
      })
      .catch(err => setError(err.message));
  };
  useEffect(() => load(), []);
  useEffect(() => subscribeProjectChange(() => load()), []);
  // Keeps deep links (including the product tour hand-off) working while the page is already mounted.
  useEffect(() => { setSelected(searchParams.get('article')); }, [searchParams]);
  const visible = useMemo(() => articles.filter(article => {
    const matchesCategory = category === 'All' || article.category === category;
    const steps = article.steps.map(step => `${step.title} ${step.detail} ${step.action || ''} ${step.result || ''}`).join(' ');
    const issues = article.issues.map(issue => `${issue.question} ${issue.answer}`).join(' ');
    const text = `${article.title} ${article.summary} ${article.body.join(' ')} ${article.why || ''} ${steps} ${issues} ${article.tips.join(' ')}`.toLowerCase();
    return matchesCategory && (!query.trim() || text.includes(query.toLowerCase().trim()));
  }), [articles, category, query]);
  const applyParams = (nextCategory: string, nextArticle: string | null) => {
    const params: Record<string, string> = {};
    if (nextCategory !== 'All') params.category = nextCategory;
    if (nextArticle) params.article = nextArticle;
    setSearchParams(params);
  };
  const selectCategory = (_: React.SyntheticEvent, value: string) => { setCategory(value); setSelected(null); applyParams(value, null); };
  const openArticle = (id: string) => { setSelected(id); applyParams(category, id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const closeArticle = () => { setSelected(null); applyParams(category, null); };
  const activeArticle = selected ? articles.find(article => article.id === selected) : undefined;
  const browsingAll = category === 'All' && !query.trim();
  const featured = browsingAll ? articles.find(article => article.featured) : undefined;
  const listed = featured ? visible.filter(article => article.id !== featured.id) : visible;

  if (activeArticle) return <GuideDetail article={activeArticle} viewer={viewer} onBack={closeArticle} />;

  return <Box>
    <PageHeader
      title="Help & Guidance"
      subtitle="Guided workflows for the consent work you are responsible for in ASSERTION."
      action={(
          <Stack data-tour-id="help-launcher" direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search help"
            aria-label="Search help articles"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Button variant="outlined" startIcon={<RocketLaunchOutlinedIcon />} onClick={startTour} sx={{ whiteSpace: 'nowrap' }}>
            Take product tour
          </Button>
        </Stack>
      )}
    />
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

    <Paper sx={{ overflow: 'hidden', mb: 3 }}>
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, background: 'linear-gradient(135deg, #0B1220 0%, #1B3A94 100%)', color: '#FFFFFF' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'flex-end' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: alpha('#FFFFFF', 0.7) }}>How ASSERTION works</Typography>
            <Typography variant="h5" sx={{ color: '#FFFFFF', mt: 0.5 }}>From authority information to trusted, actionable consent work</Typography>
            <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.75), mt: 0.75, maxWidth: 620 }}>
              Six stages carry a consent from creation to a defensible audit record. Every stage has a guide below.
            </Typography>
          </Box>
          {viewer && (
            <Box sx={{ px: 2, py: 1.25, borderRadius: 1.5, bgcolor: alpha('#FFFFFF', 0.1), border: '1px solid', borderColor: alpha('#FFFFFF', 0.2), flexShrink: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <BadgeOutlinedIcon fontSize="small" sx={{ color: alpha('#FFFFFF', 0.8) }} />
                <Box>
                  <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.7), display: 'block', lineHeight: 1.3 }}>Viewing guidance for</Typography>
                  <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>{viewer.role_label}</Typography>
                  {viewer.project_name && <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.7) }}>{viewer.project_name}</Typography>}
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.5 }}>
        {workflow.map((stage, index) => (
          <Box key={stage.step} component={RouterLink} to={stage.route} sx={{ textDecoration: 'none', p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: index === workflow.length - 1 ? alpha('#16A34A', 0.05) : 'background.paper', transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: 'primary.main', boxShadow: '0 2px 10px rgba(15, 23, 42, 0.08)' } }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'primary.main' }}>{`0${index + 1}`}</Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">{stage.step}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>{stage.detail}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>

    {featured && (
      <Paper
        onClick={() => openArticle(featured.id)}
        sx={{ mb: 3, p: { xs: 2.5, md: 3 }, cursor: 'pointer', borderColor: alpha('#2450C6', 0.35), background: `linear-gradient(120deg, ${alpha('#2450C6', 0.07)} 0%, rgba(255,255,255,0) 60%)`, transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: 'primary.main', boxShadow: '0 8px 22px rgba(15, 23, 42, 0.10)' } }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#2450C6', 0.1), color: 'primary.main', display: 'flex', flexShrink: 0 }}>
            <RocketLaunchOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="overline" sx={{ color: 'primary.main' }}>Start here</Typography>
            <Typography variant="h6" sx={{ mt: 0.25 }}>{featured.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6, maxWidth: 720 }}>{featured.summary}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {featured.steps.length} steps{featured.read_time ? ` · ${featured.read_time}` : ''}
            </Typography>
          </Box>
          <Button variant="contained" endIcon={<ChevronRightIcon />} sx={{ flexShrink: 0 }}>Open guide</Button>
        </Stack>
      </Paper>
    )}

    {tasks.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Things you can do</Typography>
          <Typography variant="body2" color="text.secondary">Jump straight to the task you have in front of you.</Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {tasks.map(task => {
            const article = articles.find(item => item.id === task.article);
            const accent = accentFor(article?.category || 'Getting Started');
            return (
              <Paper key={task.id} onClick={() => openArticle(task.article)} sx={{ p: 2.25, cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s', '&:hover': { borderColor: alpha(accent, 0.5), boxShadow: '0 8px 22px rgba(15, 23, 42, 0.10)', transform: 'translateY(-2px)' } }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(accent, 0.1), color: accent, display: 'flex', flexShrink: 0 }}>{TASK_ICONS[task.icon] || <MenuBookOutlinedIcon />}</Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{task.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>{task.description}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    {article && article.steps.length > 0 ? `${article.steps.length} steps` : 'Reference'}
                    {article?.read_time ? ` · ${article.read_time}` : ''}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" fontWeight={700} sx={{ color: accent }}>Open guide</Typography>
                  <ChevronRightIcon fontSize="small" sx={{ color: accent }} />
                </Stack>
              </Paper>
            );
          })}
        </Box>
      </Box>
    )}

    <Paper sx={{ px: { xs: 1, md: 1.5 }, mb: 2 }}>
      <Tabs value={category} onChange={selectCategory} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Help categories">{categories.map(item => <Tab key={item} value={item} label={item} />)}</Tabs>
    </Paper>
    <Alert severity="info" icon={<BadgeOutlinedIcon fontSize="small" />} sx={{ mb: 2.5 }}>
      This guidance is tailored to the projects and permissions available to your account{viewer ? ` as ${viewer.role_label}` : ''}. Topics you cannot act on are not shown.
    </Alert>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
      {listed.map(article => {
        const accent = accentFor(article.category);
        return (
          <Paper key={article.id} onClick={() => openArticle(article.id)} sx={{ cursor: 'pointer', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: alpha(accent, 0.5), boxShadow: '0 8px 22px rgba(15, 23, 42, 0.10)' } }}>
            <Box sx={{ height: 3, bgcolor: accent }} />
            <Box sx={{ p: 2.25, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                <Chip size="small" label={article.category} sx={{ bgcolor: alpha(accent, 0.1), color: accent, fontWeight: 600 }} />
                <Typography variant="caption" color="text.secondary">
                  {article.steps.length > 0 ? `${article.steps.length} steps` : 'Reference'}
                  {article.read_time ? ` · ${article.read_time}` : ''}
                </Typography>
              </Stack>
              <Typography variant="h6">{article.title}</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>{article.summary}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: accent }}>{article.steps.length > 0 ? 'Open guide' : 'Read more'}</Typography>
                <ChevronRightIcon fontSize="small" sx={{ color: accent }} />
              </Stack>
            </Box>
          </Paper>
        );
      })}
    </Box>
    {!listed.length && <Paper sx={{ p: 5, textAlign: 'center' }}>
      <SearchIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
      <Typography variant="h6" sx={{ mt: 1 }}>No guidance matches that search.</Typography>
      <Typography color="text.secondary">Try another term or choose All categories.</Typography>
    </Paper>}
  </Box>;
}
